import React, { useState, useEffect, useCallback } from 'react';
import { Users, UserPlus, Mail, Phone, Sparkles, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'nail_tech',
    skills: []
  });
  const [selectedSkills, setSelectedSkills] = useState([]);

  const allSkills = ['nails', 'spray_tan', 'facial', 'hair', 'makeup'];
  const roles = [
    { value: 'nail_tech', label: 'Nail Technician' },
    { value: 'esthetician', label: 'Esthetician' },
    { value: 'hair_stylist', label: 'Hair Stylist' },
    { value: 'makeup_artist', label: 'Makeup Artist' },
    { value: 'admin', label: 'Administrator' }
  ];

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees`);
      setEmployees(res.data);
      setLoading(false);
    } catch (error) {
      // Log error in development only
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching employees:', error);
      }
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }

    if (selectedSkills.length === 0) {
      alert('Please select at least one skill');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/employees`, {
        ...formData,
        skills: selectedSkills
      });

      setShowAddModal(false);
      setFormData({ name: '', email: '', phone: '', role: 'nail_tech', skills: [] });
      setSelectedSkills([]);
      fetchEmployees();
      alert('Employee added successfully!');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error adding employee:', error);
      }
      alert('Error adding employee');
    }
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm('Are you sure you want to remove this employee?')) return;

    try {
      await axios.delete(`${API_URL}/api/employees/${id}`);
      fetchEmployees();
      alert('Employee removed successfully!');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error deleting employee:', error);
      }
      alert('Error removing employee');
    }
  };

  const toggleSkill = (skill) => {
    if (selectedSkills.includes(skill)) {
      setSelectedSkills(selectedSkills.filter(s => s !== skill));
    } else {
      setSelectedSkills([...selectedSkills, skill]);
    }
  };

  const getSkillIcon = (skill) => {
    const icons = {
      nails: '💅',
      spray_tan: '☀️',
      facial: '✨',
      hair: '💇',
      makeup: '💄'
    };
    return icons[skill] || '💎';
  };

  const getStatusColor = (status) => {
    const colors = {
      available: 'bg-green-500/10 text-green-600 border-green-500/20',
      busy: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
      off_duty: 'bg-gray-500/10 text-gray-600 border-gray-500/20'
    };
    return colors[status] || colors.available;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Team Members</h1>
          <p className="text-muted-foreground mt-1">Manage your glam squad ✨</p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-amber-500 to-amber-600"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Team</p>
                <p className="text-3xl font-bold mt-1">{employees.length}</p>
              </div>
              <Users className="w-10 h-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Available Now</p>
                <p className="text-3xl font-bold mt-1">
                  {employees.filter(e => e.availability_status === 'available').length}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <span className="text-2xl">✅</span>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">On Duty</p>
                <p className="text-3xl font-bold mt-1">
                  {employees.filter(e => e.availability_status !== 'off_duty').length}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                <span className="text-2xl">💼</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employees Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {employees.map((employee) => (
          <Card key={employee.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-lg">
                    {employee.name.charAt(0)}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{employee.name}</CardTitle>
                    <p className="text-sm text-muted-foreground capitalize">
                      {employee.role.replace('_', ' ')}
                    </p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(employee.availability_status)}`}>
                  {employee.availability_status.replace('_', ' ')}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span className="truncate">{employee.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="w-4 h-4" />
                {employee.phone}
              </div>

              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Skills:</p>
                <div className="flex flex-wrap gap-2">
                  {employee.skills.map((skill) => (
                    <span
                      key={`${employee.id}-${skill}`}
                      className="text-xs px-2 py-1 bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded-full flex items-center gap-1"
                    >
                      <span>{getSkillIcon(skill)}</span>
                      {skill.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>

              <div className="pt-3 border-t flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {employee.weekly_hours}h this week
                </span>
                <button
                  onClick={() => deleteEmployee(employee.id)}
                  className="text-red-500 hover:text-red-600 p-1"
                  title="Remove employee"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        ))}

        {employees.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No team members yet</p>
              <p className="text-muted-foreground mb-4">Add your first employee to get started</p>
              <Button onClick={() => setShowAddModal(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Add Employee
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Employee Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Team Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddEmployee} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Christina Sunshine"
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="employee@monthlyglowonthego.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="904-672-9966"
                required
              />
            </div>

            <div>
              <Label htmlFor="role">Role</Label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
              >
                {roles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Skills * (Select all that apply)</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {allSkills.map((skill) => (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => toggleSkill(skill)}
                    className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                      selectedSkills.includes(skill)
                        ? 'bg-amber-500/20 border-amber-500 text-amber-700 dark:text-amber-300'
                        : 'bg-background hover:bg-accent'
                    }`}
                  >
                    <span className="mr-1">{getSkillIcon(skill)}</span>
                    {skill.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600">
                Add Employee
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;